import React, { useEffect, useMemo, useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import SeatSelectionModal from './SeatSelectionModal.jsx';
import FormSelect from '../components/FormSelect.jsx';
import {
  assignSeatWithStatus,
  confirmSeatUpgrade,
  createStudent,
  getLibraries,
  getPaymentPlans,
  getSeatsData,
  requestSeatUpgrade,
  updateStudent,
} from '../services/api.js';
import {
  normalizeSubscriptionType,
  subscriptionTypeDays,
  subscriptionTypeMonths,
  subscriptionTypeOptions,
} from '../constants/subscriptionTypes.js';
import { formatPhoneForInput, formatPhoneForWhatsApp, getPhoneValidationError } from '../utils/phoneUtils.js';
import { validateStudentEmail } from '../utils/emailValidation.js';
import { getTenantSessionScope } from '../api/api1.js';
import '../styles/pages/students.css';

const emptyStudentFormCache = (scope) => ({
  libraries: null,
  seats: null,
  plans: null,
  ts: 0,
  scope,
});
export let studentFormCache = emptyStudentFormCache('');
export const CACHE_TTL = 5 * 60 * 1000;

export const getStudentFormCache = () => {
  const scope = getTenantSessionScope();
  if (studentFormCache.scope !== scope) studentFormCache = emptyStudentFormCache(scope);
  return studentFormCache;
};

export const prefetchStudentFormData = async () => {
  try {
    const scope = getTenantSessionScope();
    const currentCache = getStudentFormCache();
    const now = Date.now();
    if (
      currentCache.libraries &&
      currentCache.seats &&
      currentCache.plans &&
      now - currentCache.ts < CACHE_TTL
    ) {
      return currentCache;
    }
    const [libraryResponse, seatResponse, planResponse] = await Promise.all([
      getLibraries().catch(() => []),
      getSeatsData().catch(() => ({ seats: [], rooms: [] })),
      getPaymentPlans().catch(() => []),
    ]);
    const nextLibraries = (Array.isArray(libraryResponse) ? libraryResponse : libraryResponse?.libraries || libraryResponse?.data || []);
    const nextSeats = (Array.isArray(seatResponse?.seats) ? seatResponse.seats : Array.isArray(seatResponse) ? seatResponse : []);
    const nextPlans = (Array.isArray(planResponse) ? planResponse : planResponse?.payment_plans || planResponse?.paymentPlans || planResponse?.data || []);
    if (scope !== getTenantSessionScope()) return getStudentFormCache();
    studentFormCache = {
      libraries: nextLibraries,
      seats: nextSeats,
      plans: nextPlans,
      ts: Date.now(),
      scope,
    };
    return studentFormCache;
  } catch (e) {
    return getStudentFormCache();
  }
};

const todayInput = () => new Date().toISOString().slice(0, 10);

const formatDateForInput = value => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
};

const addMonths = (value, months) => {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return '';
  const day = date.getDate();
  date.setMonth(date.getMonth() + months);
  if (date.getDate() !== day) date.setDate(0);
  return date.toISOString().slice(0, 10);
};

const computeEndDate = (start, subscription) => addMonths(start, subscriptionTypeMonths(subscription));

const normalizeCategory = value => {
  const category = String(value || '').toLowerCase();
  return category.includes('non') ? 'non_ac' : 'ac';
};

const normalizePlanType = value => {
  return String(value || 'general').toLowerCase() === 'reserved' ? 'reserved' : 'general';
};

const generateStudentCode = () => {
  try {
    const tenant = JSON.parse(localStorage.getItem('tenantinfo') || '{}');
    const prefix = String(tenant.subdomain || tenant.name || 'STU').slice(0, 3).toUpperCase();
    return `${prefix}${Math.floor(100000 + Math.random() * 900000)}`;
  } catch {
    return `STU${Math.floor(100000 + Math.random() * 900000)}`;
  }
};

const normalizeList = (response, keys = []) => {
  if (Array.isArray(response)) return response;
  for (const key of keys) {
    if (Array.isArray(response?.[key])) return response[key];
  }
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const planIdOf = plan => plan?.plan_id ?? plan?.planid ?? plan?.planId ?? plan?.id;
const planDaysOf = plan => Number(plan?.durationdays ?? plan?.duration_days ?? plan?.durationDays ?? 0);
const planCategoryOf = plan => normalizeCategory(plan?.seat_category ?? plan?.seatcategory ?? plan?.seatCategory);

const studentIdOf = student => student?.student_id || student?.studentid || student?.id || student?._id;

const validateName = name => {
  if (!name || !name.trim()) return 'Name is required';
  if (!/^[a-zA-Z\s'.-]+$/.test(name)) {
    return 'Name can only contain letters, spaces, hyphens, apostrophes, and periods';
  }
  if (name.trim().length < 2) return 'Name must be at least 2 characters';
  return null;
};

const buildInitialForm = student => {
  const source = student || {};
  const phone = source.phone || source.contact_no || source.contactno || '';
  const subscription = normalizeSubscriptionType(
    source.subscription_type || source.subscriptiontype || source.subscription || 'monthly'
  );
  const start = formatDateForInput(source.subscription_start || source.subscriptionstart);
  const end = formatDateForInput(source.subscription_end || source.subscriptionend);

  return {
    name: source.name || source.full_name || '',
    email: source.email || '',
    phone: formatPhoneForInput(phone),
    subscription,
    seatCategory: normalizeCategory(source.seat_category || source.seatcategory || source.seatCategory),
    seat_id: source.seat_id || source.seatid || '',
    seat_number: source.seat_number_display || source.seatnumber || source.seat_number || source.seat || '',
    library_id: source.library_id || source.libraryId || '',
    address: source.address || '',
    course: source.course || '',
    institute_name: source.institute_name || source.institutename || '',
    subscription_start: start,
    subscription_end: end || (start ? computeEndDate(start, subscription) : ''),
    admission_date: formatDateForInput(
      source.admission_date || source.admissiondate || source.joined_date || source.created_at
    ) || todayInput(),
    status: String(source.status || 'active').toLowerCase(),
    plan_type: normalizePlanType(source.plan_type || source.seat_type),
    plan_id: source.plan_id || source.planid || '',
  };
};

export default function StudentEditorModal({ student = null, toast, onClose, onSaved, onPayStudent, inline = false }) {
  const editing = Boolean(student);
  const [form, setForm] = useState(() => buildInitialForm(student));
  const [libraries, setLibraries] = useState([]);
  const [seats, setSeats] = useState([]);
  const [allPlans, setAllPlans] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSeatModal, setShowSeatModal] = useState(false);
  const [pendingUpgrade, setPendingUpgrade] = useState(null);
  const [studentCode] = useState(
    () => student?.user_id || student?.userid || student?.student_id_code || generateStudentCode()
  );

  useEffect(() => {
    if (inline || !onClose) return undefined;

    const handleKeyDown = event => {
      if (event.key === 'Escape' && !saving) onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [inline, onClose, saving]);

  useEffect(() => {
    let active = true;

    const loadReferenceData = async () => {
      setLoading(true);
      try {
        const sessionScope = getTenantSessionScope();
        const currentCache = getStudentFormCache();
        const now = Date.now();
        if (
          currentCache.libraries &&
          currentCache.seats &&
          currentCache.plans &&
          now - currentCache.ts < CACHE_TTL
        ) {
          if (!active) return;
          setLibraries(currentCache.libraries);
          setSeats(currentCache.seats);
          setAllPlans(currentCache.plans);
          setLoading(false);
          if (student) {
            const studentLibraryName = String(
              student.library_name_display || student.library_name || student.library || ''
            ).toLowerCase();
            const match = currentCache.libraries.find(library => {
              const name = String(library.library_name || library.name || '').toLowerCase();
              return name && name === studentLibraryName;
            });
            if (match) {
              setForm(prev => prev.library_id ? prev : ({ ...prev, library_id: match.id ?? match.library_id ?? '' }));
            }
          }
          return;
        }

        const [libraryResponse, seatResponse, planResponse] = await Promise.all([
          getLibraries().catch(() => []),
          getSeatsData(true).catch(() => getSeatsData().catch(() => ({ seats: [], rooms: [] }))),
          getPaymentPlans().catch(() => []),
        ]);

        if (!active || sessionScope !== getTenantSessionScope()) return;
        const nextLibraries = normalizeList(libraryResponse, ['libraries']);
        const nextSeats = normalizeList(seatResponse?.seats ?? seatResponse, ['seats']);
        const nextPlans = normalizeList(planResponse, ['payment_plans', 'paymentPlans']);

        studentFormCache = {
          libraries: nextLibraries,
          seats: nextSeats,
          plans: nextPlans,
          ts: Date.now(),
          scope: sessionScope,
        };

        setLibraries(nextLibraries);
        setSeats(nextSeats);
        setAllPlans(nextPlans);

        if (student) {
          const studentLibraryName = String(
            student.library_name_display || student.library_name || student.library || ''
          ).toLowerCase();
          const match = nextLibraries.find(library => {
            const name = String(library.library_name || library.name || '').toLowerCase();
            return name && name === studentLibraryName;
          });
          if (match) {
            setForm(prev => prev.library_id ? prev : ({ ...prev, library_id: match.id ?? match.library_id ?? '' }));
          }
        }
      } catch (error) {
        if (active) toast?.(error?.message || 'Could not load student form data');
      } finally {
        if (active) setLoading(false);
      }
    };

    loadReferenceData();
    return () => {
      active = false;
    };
  }, [student, toast]);

  const filteredPlans = useMemo(() => {
    if (!form.library_id || !form.subscription) return [];
    const targetDays = subscriptionTypeDays(form.subscription);
    const targetCategory = form.seatCategory;

    const matches = allPlans.filter(plan => {
      if (planDaysOf(plan) !== targetDays) return false;

      const category = planCategoryOf(plan);
      const isReservedPlan = String(plan?.seat_category || plan?.seatcategory || '').toLowerCase().includes('reserved');
      const isCategoryMatch = category === targetCategory;

      if (form.plan_type === 'reserved') {
        return isReservedPlan || isCategoryMatch;
      }
      return !isReservedPlan && isCategoryMatch;
    });

    return matches
      .filter(plan => {
        const libraryId = plan.library_id ?? plan.libraryId;
        return !libraryId || String(libraryId) === '0' || String(libraryId) === String(form.library_id);
      })
      .sort((a, b) => {
        const aLibrary = a.library_id ?? a.libraryId;
        const bLibrary = b.library_id ?? b.libraryId;
        const aSpecific = aLibrary && String(aLibrary) === String(form.library_id);
        const bSpecific = bLibrary && String(bLibrary) === String(form.library_id);
        if (Boolean(aSpecific) !== Boolean(bSpecific)) return aSpecific ? -1 : 1;
        return 0;
      });
  }, [allPlans, form.library_id, form.subscription, form.seatCategory, form.plan_type]);

  useEffect(() => {
    if (!filteredPlans.length) {
      if (form.plan_id) setForm(prev => ({ ...prev, plan_id: '' }));
      return;
    }

    const stillAvailable = filteredPlans.some(plan => String(planIdOf(plan)) === String(form.plan_id));
    if (!stillAvailable) {
      setForm(prev => ({ ...prev, plan_id: String(planIdOf(filteredPlans[0])) }));
    }
  }, [filteredPlans, form.plan_id]);

  const selectedPlan = useMemo(
    () => filteredPlans.find(plan => String(planIdOf(plan)) === String(form.plan_id)) || null,
    [filteredPlans, form.plan_id]
  );

  const selectedSeat = useMemo(
    () => seats.find(seat => String(seat.seat_id ?? seat.id ?? seat.seatid) === String(form.seat_id)) || null,
    [seats, form.seat_id]
  );

  const selectedSeatLabel = useMemo(() => {
    if (!form.seat_id && !form.seat_number) return '';
    if (selectedSeat) return selectedSeat.seat_number || selectedSeat.seatno || selectedSeat.seat_no || form.seat_number;
    return form.seat_number;
  }, [form.seat_id, form.seat_number, selectedSeat]);

  const updateField = (name, value) => {
    setErrors(prev => ({ ...prev, [name]: '' }));
    setForm(prev => {
      const next = { ...prev, [name]: value };
      if (name === 'phone') next.phone = formatPhoneForInput(value);
      if (name === 'subscription_start' || name === 'subscription') {
        next.subscription_end = computeEndDate(
          name === 'subscription_start' ? value : prev.subscription_start,
          name === 'subscription' ? value : prev.subscription
        );
      }
      if (name === 'seatCategory') {
        next.seat_id = '';
        next.seat_number = '';
      }
      return next;
    });
  };

  const handleSeatSelected = seat => {
    const seatId = seat.seat_id ?? seat.id ?? seat.seatid;
    const selectionType = seat.selection_type || seat.seat_status || seat.status;
    setErrors(prev => ({ ...prev, seat_id: '' }));
    setForm(prev => ({
      ...prev,
      seat_id: seatId,
      seat_number: seat.seat_number || seat.seatnumber || seat.seatno || prev.seat_number,
      library_id: seat.library_id || seat.libraryId || prev.library_id,
      seatCategory: normalizeCategory(seat.category || seat.seat_category || prev.seatCategory),
      plan_type: String(selectionType || '').toLowerCase() === 'reserved' ? 'reserved' : prev.plan_type,
    }));
    setShowSeatModal(false);
  };

  const validate = () => {
    const nextErrors = {};
    const nameError = validateName(form.name);
    if (nameError) nextErrors.name = nameError;

    const emailError = validateStudentEmail(form.email);
    if (emailError) nextErrors.email = emailError;

    const phoneError = getPhoneValidationError(form.phone);
    if (phoneError) nextErrors.phone = phoneError;

    if (!form.library_id) nextErrors.library_id = 'Please select a library';
    if (!form.seat_id) nextErrors.seat_id = 'Please select a seat';
    if (!form.address.trim()) nextErrors.address = 'Address is required';
    if (!form.subscription_start) nextErrors.subscription_start = 'Start date is required';
    if (!form.subscription_end) nextErrors.subscription_end = 'End date is required';
    if (!form.plan_id) nextErrors.plan_id = 'No payment plan is available for these selections';

    return nextErrors;
  };

  const buildPayload = () => ({
    name: form.name.trim(),
    email: form.email.trim(),
    phone: formatPhoneForWhatsApp(form.phone),
    subscription: form.subscription,
    seatCategory: form.seatCategory,
    seatcategory: form.seatCategory,
    seat_id: form.seat_id,
    seatid: form.seat_id,
    library_id: form.library_id,
    address: form.address.trim(),
    course: form.course.trim(),
    institutename: form.institute_name.trim(),
    institute_name: form.institute_name.trim(),
    subscriptionstart: form.subscription_start,
    subscription_start: form.subscription_start,
    subscriptionend: form.subscription_end,
    subscription_end: form.subscription_end,
    admissiondate: form.admission_date,
    admission_date: form.admission_date,
    userid: studentCode,
    studentidcode: studentCode,
    status: form.status,
    plan_type: form.plan_type,
    plan_id: form.plan_id ? Number(form.plan_id) : null,
  });

  const finish = (message, savedStudent) => {
    toast?.(message);
    onSaved?.(savedStudent, message);
    onClose?.();
  };

  const completeSeatUpgrade = async mode => {
    if (!pendingUpgrade?.upgrade_id) return;
    const currentStudentId = studentIdOf(student);
    setSaving(true);
    try {
      const result = await confirmSeatUpgrade(currentStudentId, pendingUpgrade.upgrade_id, mode);
      setPendingUpgrade(null);
      finish('Seat upgrade updated successfully', { ...student, ...result });

      if (mode === 'pay_now' && Number(result?.amount_due || 0) > 0) {
        onPayStudent?.(currentStudentId);
      }
    } catch (error) {
      setErrors(prev => ({
        ...prev,
        submit: error?.error || error?.message || 'Could not confirm the seat upgrade',
      }));
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async event => {
    event.preventDefault();
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setSaving(true);
    const payload = buildPayload();

    try {
      if (!editing) {
        const result = await createStudent(payload);
        const createdId = result?.studentId || result?.student_id || result?.id;
        // Dispatch event to notify other components
        window.dispatchEvent(new CustomEvent('studentDataUpdated', {
          detail: { studentId: createdId, action: 'created' }
        }));
        finish('Student added successfully', {
          ...payload,
          id: createdId || Date.now(),
          student_id: createdId,
          studentId: createdId,
          seatnumber: selectedSeatLabel,
          room_name: selectedSeat?.room_name,
        });
        return;
      }

      const currentStudentId = studentIdOf(student);
      if (!currentStudentId) throw new Error('Student ID not found');

      const originalPlanType = normalizePlanType(student.plan_type || student.seat_type);
      const nextPlanType = normalizePlanType(form.plan_type);
      const planTypeChanged = originalPlanType !== nextPlanType;
      const originalSeatId = student.seat_id || student.seatid || '';
      const seatChanged = form.seat_id && String(form.seat_id) !== String(originalSeatId);

      if (planTypeChanged) {
        const basicPayload = { ...payload };
        delete basicPayload.seatCategory;
        delete basicPayload.seatcategory;
        delete basicPayload.plan_type;
        delete basicPayload.seat_id;
        delete basicPayload.seatid;
        await updateStudent(currentStudentId, basicPayload);

        const quote = await requestSeatUpgrade(currentStudentId, nextPlanType, null);
        if (quote?.requires_payment) {
          setPendingUpgrade(quote);
          return;
        }

        finish('Student details updated successfully', {
          ...student,
          ...payload,
          plan_type: nextPlanType,
          seat_status: nextPlanType === 'reserved' ? 'reserved' : 'occupied',
        });
        return;
      }

      const updated = await updateStudent(currentStudentId, payload);
      if (seatChanged) {
        await assignSeatWithStatus(currentStudentId, form.seat_id, form.plan_type === 'reserved' ? 'reserved' : 'occupied');
      }

      // Dispatch event to notify other components
      window.dispatchEvent(new CustomEvent('studentDataUpdated', {
        detail: { studentId: currentStudentId, action: 'updated' }
      }));

      finish('Student details updated successfully', {
        ...student,
        ...payload,
        ...updated,
        id: currentStudentId,
        student_id: currentStudentId,
        studentId: currentStudentId,
        subscription_type: form.subscription,
        seat_category: form.seatCategory,
        contact_no: payload.phone,
        seat_number: selectedSeatLabel,
        seatnumber: selectedSeatLabel,
        room_name: selectedSeat?.room_name || student.room_name,
      });
    } catch (error) {
      setErrors(prev => ({
        ...prev,
        submit: error?.error || error?.message || `Could not ${editing ? 'update' : 'add'} student`,
      }));
    } finally {
      setSaving(false);
    }
  };

  const title = editing ? 'Edit Student' : 'Add Student';

  return (
    <div
      className={inline ? 'student-form-embed' : 'modal-backdrop'}
      onMouseDown={inline ? undefined : event => event.target === event.currentTarget && onClose?.()}
    >
      <div className={inline ? 'student-editor-modal student-form-card' : 'modal student-editor-modal'}>
        <div className="modal-head">
          <div>
            <span className="eyebrow">STUDENT RECORD</span>
            <h2>{title}</h2>
          </div>
          {!inline && (
            <button
              type="button"
              className="close-button"
              onClick={onClose}
              aria-label="Close student form"
              title="Close"
            >
              <FaTimes aria-hidden="true" />
            </button>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          <>
            <div className="form-grid">
                {inline && (
                  <div className="editor-form-section">
                    <span>1</span>
                    <div><b>Student details</b><small>Who is enrolling?</small></div>
                  </div>
                )}
                <label>
                  Name <em>*</em>
                  <input value={form.name} onChange={event => updateField('name', event.target.value)} placeholder="Student full name" />
                  {errors.name && <span className="editor-error">{errors.name}</span>}
                </label>

                <label>
                  Email <em>*</em>
                  <input type="email" value={form.email} onChange={event => updateField('email', event.target.value)} placeholder="student@gmail.com" />
                  {errors.email && <span className="editor-error">{errors.email}</span>}
                </label>

                <label>
                  Phone <em>*</em>
                  <div className="phone-input">
                    <span>+91</span>
                    <input value={form.phone} onChange={event => updateField('phone', event.target.value)} placeholder="9876543210" inputMode="numeric" />
                  </div>
                  {errors.phone && <span className="editor-error">{errors.phone}</span>}
                </label>

                <label>
                  Admission date
                  <input type="date" value={form.admission_date} onChange={event => updateField('admission_date', event.target.value)} />
                </label>

                {inline && (
                  <div className="editor-form-section">
                    <span>2</span>
                    <div><b>Seat &amp; library</b><small>Choose where the student will sit.</small></div>
                  </div>
                )}
                <label>
                  Library <em>*</em>
                  <FormSelect
                    value={form.library_id}
                    onChange={value => updateField('library_id', value)}
                    placeholder={loading && libraries.length === 0 ? 'Loading...' : 'Select library'}
                    options={[
                      { value: '', label: loading && libraries.length === 0 ? 'Loading...' : 'Select library' },
                      ...libraries.map(library => ({
                        value: library.id ?? library.library_id,
                        label: library.library_name || library.name,
                      })),
                    ]}
                  />
                  {errors.library_id && <span className="editor-error">{errors.library_id}</span>}
                </label>

                <label>
                  Seat <em>*</em>
                  <div className="seat-picker-row">
                    <input
                      readOnly
                      value={selectedSeatLabel || ''}
                      placeholder={loading && seats.length === 0 ? 'Loading seats...' : 'Select a seat'}
                      onClick={() => setShowSeatModal(true)}
                      onKeyDown={event => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          setShowSeatModal(true);
                        }
                      }}
                      aria-haspopup="dialog"
                      aria-label="Select a seat"
                    />
                  </div>
                  {errors.seat_id && <span className="editor-error">{errors.seat_id}</span>}
                </label>

                <label>
                  Seat category
                  <FormSelect
                    value={form.seatCategory}
                    onChange={value => updateField('seatCategory', value)}
                    options={[
                      { value: 'ac', label: 'AC' },
                      { value: 'non_ac', label: 'Non-AC' },
                    ]}
                  />
                </label>

                <label>
                  Seat type
                  <FormSelect
                    value={form.plan_type}
                    onChange={value => updateField('plan_type', value)}
                    options={[
                      { value: 'general', label: 'General' },
                      { value: 'reserved', label: 'Reserved' },
                    ]}
                  />
                </label>

                {inline && (
                  <div className="editor-form-section">
                    <span>3</span>
                    <div><b>Subscription</b><small>Set the membership period and status.</small></div>
                  </div>
                )}
                <label>
                  Subscription type <em>*</em>
                  <FormSelect
                    value={form.subscription}
                    onChange={value => updateField('subscription', value)}
                    options={subscriptionTypeOptions().map(option => ({
                      value: option.value,
                      label: option.label,
                    }))}
                  />
                </label>

                <label>
                  Status
                  <FormSelect
                    value={form.status}
                    onChange={value => updateField('status', value)}
                    options={[
                      { value: 'active', label: 'Active' },
                      { value: 'inactive', label: 'Inactive' },
                    ]}
                  />
                </label>

                <label>
                  Subscription start <em>*</em>
                  <input type="date" value={form.subscription_start} onChange={event => updateField('subscription_start', event.target.value)} />
                  {errors.subscription_start && <span className="editor-error">{errors.subscription_start}</span>}
                </label>

                <label>
                  Subscription end <em>*</em>
                  <input type="date" value={form.subscription_end} onChange={event => updateField('subscription_end', event.target.value)} />
                  {errors.subscription_end && <span className="editor-error">{errors.subscription_end}</span>}
                </label>

                {inline && (
                  <div className="editor-form-section">
                    <span>4</span>
                    <div><b>Payment &amp; additional details</b><small>Choose a plan and add optional student information.</small></div>
                  </div>
                )}
                <label className="editor-field-full">
                  Payment plan <em>*</em>
                  <FormSelect
                    value={form.plan_id}
                    onChange={value => updateField('plan_id', value)}
                    placeholder={loading && filteredPlans.length === 0 ? 'Loading plans...' : filteredPlans.length ? 'Select payment plan' : 'No matching plans available'}
                    options={[
                      { value: '', label: loading && filteredPlans.length === 0 ? 'Loading plans...' : filteredPlans.length ? 'Select payment plan' : 'No matching plans available' },
                      ...filteredPlans.map(plan => ({
                        value: planIdOf(plan),
                        label: `${plan.plan_name || plan.planname || 'Plan'} • ₹${Number(plan.amount || 0).toLocaleString('en-IN')} • ${planDaysOf(plan)} days`,
                      })),
                    ]}
                  />
                  {errors.plan_id && <span className="editor-error">{errors.plan_id}</span>}
                </label>

                <label className="editor-field-full">
                  Address <em>*</em>
                  <input value={form.address} onChange={event => updateField('address', event.target.value)} placeholder="Full address" />
                  {errors.address && <span className="editor-error">{errors.address}</span>}
                </label>

                <label className="editor-field-span-2">
                  Course
                  <input value={form.course} onChange={event => updateField('course', event.target.value)} placeholder="Optional" />
                </label>

                <label className="editor-field-span-2">
                  Institute
                  <input value={form.institute_name} onChange={event => updateField('institute_name', event.target.value)} placeholder="Optional" />
                </label>
              </div>

              {selectedPlan && (
                <div className="editor-plan-summary">
                  <div>
                    <span>Selected plan</span>
                    <b>{selectedPlan.plan_name || selectedPlan.planname || 'Plan'}</b>
                  </div>
                  <div>
                    <span>Amount</span>
                    <b>₹{Number(selectedPlan.amount || 0).toLocaleString('en-IN')}</b>
                  </div>
                  <div>
                    <span>Duration</span>
                    <b>{planDaysOf(selectedPlan)} days</b>
                  </div>
                  <div>
                    <span>Student ID</span>
                    <b>{studentCode}</b>
                  </div>
                </div>
              )}

              {!filteredPlans.length && (
                <div className="editor-note">
                  No payment plan matches this library, subscription type, and seat category. Create a matching plan in Payment Plans first.
                </div>
              )}

              {errors.submit && <div className="editor-error editor-submit-error">{errors.submit}</div>}

              {pendingUpgrade && (
                <div className="editor-upgrade">
                  <div>
                    <span>Reserved seat upgrade</span>
                    <b>Additional payment required: ₹{Number(pendingUpgrade.amount_due || pendingUpgrade.amount || 0).toLocaleString('en-IN')}</b>
                  </div>
                  <div className="editor-upgrade-actions">
                    <button type="button" className="button ghost" disabled={saving} onClick={() => completeSeatUpgrade('pay_later')}>Pay later</button>
                    <button type="button" className="button primary" disabled={saving} onClick={() => completeSeatUpgrade('pay_now')}>Pay now</button>
                  </div>
                </div>
              )}

              <div className="modal-actions">
                <button type="button" className="button ghost" onClick={onClose} disabled={saving}>Cancel</button>
                <button type="submit" className="button primary" disabled={saving || Boolean(pendingUpgrade)}>
                  {saving ? 'Saving...' : editing ? 'Save changes' : 'Add student'}
                </button>
              </div>
            </>
        </form>
      </div>

      <SeatSelectionModal
        isOpen={showSeatModal}
        onClose={() => setShowSeatModal(false)}
        preSelectedLibrary={form.library_id}
        studentName={form.name}
        onSeatSelected={handleSeatSelected}
      />
    </div>
  );
}
